import { ref, computed, watch } from "vue";
import { defineStore, acceptHMRUpdate } from "pinia";
import { useLocalStorageCacheStore } from "@/stores/localStorageCache";
// The ONE reactive read of a `matchMedia` query — the store's `system` theme
// branch depends on the OS color scheme as a real reactive dep (th-z5i9t).
import { useMediaQuery } from "@/composables/useMediaQuery";
// The Skyline sky's persisted id set. `unifiedRouter` validates the
// `workspace` URL slot against the same array; this store owns the ref
// (`dayPhaseMode`) and the only validated write (`setDayPhaseMode`). A leaf:
// importing it here closes no cycle (th-e7hnl).
import { DAY_PHASE_MODE_IDS } from "@/constants/skylineViewOptions";
// The desktop breakpoint, shared with the public showcase page, which must not
// import this store (launch-marketing-pages M9). A leaf.
import { DESKTOP_MIN_WIDTH } from "@/constants/breakpoints";
// The layout roster `setLayout` validates against, shared with the router's
// `workspace` validator (th-hk9nl), and the one layout that draws no
// SecondaryPanel. A leaf: importing it closes no cycle. `layoutsOfferedIn` is
// the leaf's viewport-fit rule (th-5n61wn), renamed so the store's one read of
// it can carry the name its consumers know, `offeredLayouts`.
import {
  PANEL_HIDDEN_LAYOUT,
  WORKSPACE_LAYOUTS,
  offeredLayouts as layoutsOfferedIn,
} from "@/utils/workspaceLayouts";

/**
 * Presentation for each `DAY_PHASE_MODE_IDS` entry — the text label the
 * Workspace settings page's "Skyline sky" control renders. The ids come from
 * the leaf constants module; only the human-facing half lives here
 * (skyline-non-leasable-plates D5).
 */
const DAY_PHASE_PRESENTATION = Object.freeze({
  auto: { title: 'Auto' },
  dawn: { title: 'Dawn' },
  day: { title: 'Day' },
  dusk: { title: 'Dusk' },
  night: { title: 'Night' },
});

// The sky control's options, in the id set's own order: ONE list, so the
// router's validator, `setDayPhaseMode`'s whitelist and the settings page all
// read the same vocabulary. Module scope: the id import is a leaf, so no cycle forces this
// into the setup body any more (th-e7hnl).
const dayPhaseOptions = Object.freeze(
  DAY_PHASE_MODE_IDS.map((id) =>
    Object.freeze({ id, ...DAY_PHASE_PRESENTATION[id] })
  )
);

// The plain key the layout head's pre-paint theme script reads (launch-
// marketing-pages M2, `app/views/layouts/application.html.erb`): it runs before
// any module, so it cannot open the cache's JSON envelope. `setAppTheme` and
// `initAppTheme` mirror the preference into it; the envelope stays the store's
// own record.
export const THEME_MIRROR_KEY = 'tower_hunt_theme';

function mirrorThemePreference(theme) {
  try {
    window.localStorage.setItem(THEME_MIRROR_KEY, theme);
  } catch {
    // Storage blocked or full: the next visit's first frame follows the OS.
  }
}

export const useWorkspaceLayoutStore = defineStore("workspaceLayout", () => {
  const windowWidth = ref(0);
  const windowHeight = ref(0);
  const workspaceLayout = ref('fullScreen');
  // The workspace-resize broadcast: "your box moved, re-measure yourself".
  //
  // A monotonic tick, NOT a flag and NOT a bounds object. A Vue ref notifies
  // only when the assigned value differs by Object.is, so a writer that
  // re-assigns an identical value (the literal `true`, or a cached
  // LatLngBounds) goes silent after its first fire and every consumer
  // downstream stops re-measuring (th-q2i9w). Advancing a counter makes
  // "every broadcast is a distinct value" true by construction rather than by
  // discipline at each call site.
  //
  // Exposed as a readonly computed so it can ONLY advance through
  // broadcastWorkspaceResize() — a future call site that reaches for
  // `workspaceResized.value = true` fails loudly instead of quietly
  // re-arming the defect. Its consumers are the two that re-measure a
  // container of their OWN (contactDetail / companyDetail involvements); every
  // map settles from the host's own ResizeObserver instead (stores/mapHost.js),
  // so a map must NOT subscribe here.
  const workspaceResizeTick = ref(0);
  const workspaceResized = computed(() => workspaceResizeTick.value);

  /** The ONE way the workspace announces that a container box has changed. */
  function broadcastWorkspaceResize() {
    workspaceResizeTick.value += 1;
  }
  const isPortrait = computed(() => windowHeight.value > windowWidth.value);
  const isDesktop = computed(() => windowWidth.value >= DESKTOP_MIN_WIDTH);
  // Whether App.vue has measured the viewport yet (th-hk9nl). It measures only
  // once the Maps library has loaded, so the desktop shell and its map never
  // mount before the library they draw with. Until then `windowWidth` is 0 and
  // `isDesktop` reads false on ANY screen, so a first-mount decision between
  // the desktop and mobile shells waits for this rather than reading that
  // false as "mobile" (`views/MainView.vue`).
  const viewportMeasured = computed(() => windowWidth.value > 0);

  // THE ONE READ of which layouts the layout control offers (th-5n61wn): the
  // layouts this viewport can fit, plus the live layout always, by the leaf's
  // `offeredLayouts` rule. An unmeasured viewport offers the whole roster:
  // `windowHeight` is 0 until App.vue measures, and reading that 0 as "too
  // short" is the same trap `viewportMeasured` exists for.
  const offeredLayouts = computed(() =>
    viewportMeasured.value
      ? layoutsOfferedIn(workspaceLayout.value, { height: windowHeight.value })
      : WORKSPACE_LAYOUTS
  );

  // IS THERE NO SECONDARYPANEL TO DOCK A RECORD IN? (element-inspector-redesign
  // D2.) True for the DESKTOP workspace laid out `PANEL_HIDDEN_LAYOUT`, the one
  // layout that draws no panel, which is the test WorkspaceShell draws the panel
  // by. Then a record gesture made on screen looks instead of docking: the focus
  // ritual opens the record's Peek, and a glance card's identity header grows the
  // card into it. With the panel on screen, Docked wins (founder ruling 3). Below
  // the desktop breakpoint it is false: `isDesktop` is what MainView picks the
  // shell by, and the mobile shell has no record Peek (D16), so a record docks.
  //
  // THE ONE READ of this fact. The ritual's peek branch (`useFocusEntity`) and the
  // glance header's hand-off marker (`EntityGlanceTooltip`) both ask it here, so
  // the marker the card's machine reads at the press and the gesture it
  // classifies never disagree about the layout.
  const noPanelToDockIn = computed(() => isDesktop.value && workspaceLayout.value === PANEL_HIDDEN_LAYOUT);

  // Gutter state - user-customized grid positions saved from Split.js drag operations
  // These are saved/restored by WorkspaceShell and URL state
  const savedGridTemplateRows = ref(null);
  const savedGridTemplateColumns = ref(null);

  // Side-by-side width option state
  // Controls which default width is used when in sideBySide layout
  const sideBySideWidthOption = ref('default'); // 'default' | 'halfScreen'

  // CSS grid-template-columns values for each width option
  const SIDE_BY_SIDE_WIDTH_OPTIONS = {
    default: '3fr 10px 450px',    // 450px fixed secondary panel
    halfScreen: '1fr 10px 1fr'    // 50-50 split
  };

  // SETTING THE OPTION AND APPLYING IT ARE TWO DIFFERENT OPERATIONS, and the
  // split is deliberate (th-56r4w).
  //
  // `setSideBySideWidthOption` moves the LABEL only. That is what URL restore
  // wants: the URL carries `gutterCols` (the actual geometry, including a custom
  // gutter drag) alongside the option name, and re-applying the option's default
  // on restore would clobber the Actor's drag with a stock 450px.
  //
  // `applySideBySideWidthOption` is the gesture: a chooser — the layout control,
  // or a feature taking the panel over — says "be this width", and the geometry
  // follows. Every such caller MUST use it. The bug it closes: the changeset
  // review takeover called the setter alone, so the option flipped to halfScreen,
  // the URL recorded halfScreen, and the panel never moved off 450px — and
  // because the ref then already read halfScreen, the layout control's own
  // "50% screen width" button early-returned on its equality guard, leaving the
  // Actor stranded with no control that could fix it.
  function setSideBySideWidthOption(option) {
    if (['default', 'halfScreen'].includes(option)) {
      sideBySideWidthOption.value = option;
    }
  }

  /**
   * The ONE place a grid-column template reaches the workspace shell.
   *
   * Also the one place `savedGridTemplateColumns` is kept in step, because
   * `secondaryPanel.setSecondaryPanelWidth()` PREFERS that saved value over the
   * width option's default — leaving it stale would make the next panel-width
   * call restore the geometry this one just replaced.
   *
   * Returns false when there is nothing to write to (pre-mount, or a non-DOM
   * environment) so a caller can tell "applied" from "no shell yet".
   */
  function applyGridTemplateColumns(columns) {
    if (!columns || typeof document === 'undefined') return false;

    const gridContainer = document.getElementById('workspace-shell-grid-container');
    if (!gridContainer) return false;

    gridContainer.style['grid-template-columns'] = columns;
    savedGridTemplateColumns.value = columns;
    broadcastWorkspaceResize();
    return true;
  }

  /** Choose a width option AND make the layout match it. */
  function applySideBySideWidthOption(option = sideBySideWidthOption.value) {
    setSideBySideWidthOption(option);
    return applyGridTemplateColumns(
      SIDE_BY_SIDE_WIDTH_OPTIONS[sideBySideWidthOption.value],
    );
  }

  function cycleSideBySideWidth() {
    applySideBySideWidthOption(
      sideBySideWidthOption.value === 'default' ? 'halfScreen' : 'default',
    );
  }

  // ============================================================================
  // LAYOUT SELECTOR PIN STATE
  // ============================================================================
  // When pinned, the layout selector stays open in CombinedLayoutControl even
  // when the skyline button isn't hovered. Persisted as a user preference.
  const layoutSelectorPinned = ref(false);

  function setLayoutSelectorPinned(pinned) {
    layoutSelectorPinned.value = !!pinned;
    const cacheStore = useLocalStorageCacheStore();
    cacheStore.storeData(
      cacheStore.CACHE_NAMESPACES.USER_DATA,
      { setting: 'layout_selector_pinned' },
      layoutSelectorPinned.value,
      { ttl: 365 * 24 * 60 * 60 * 1000 }
    );
  }

  function toggleLayoutSelectorPinned() {
    setLayoutSelectorPinned(!layoutSelectorPinned.value);
  }

  function initLayoutSelectorPinned() {
    const cacheStore = useLocalStorageCacheStore();
    const stored = cacheStore.fetchData(
      cacheStore.CACHE_NAMESPACES.USER_DATA,
      { setting: 'layout_selector_pinned' }
    );
    if (typeof stored === 'boolean') {
      layoutSelectorPinned.value = stored;
    }
  }

  // ============================================================================
  // APP THEME MANAGEMENT (controls UI chrome + map style)
  // ============================================================================
  // appTheme: user's preference ('light' | 'dark' | 'system')
  // resolvedAppTheme: the actual computed value after resolving 'system'
  const appTheme = ref('system'); // 'light' | 'dark' | 'system'

  // The OS color scheme as a REACTIVE dep. A Pinia setup store body runs inside
  // an effectScope, so the composable's listener cleanup rides `$dispose`.
  // SSR / jsdom without matchMedia reports `false` ⇒ 'light' (th-z5i9t).
  const prefersDarkScheme = useMediaQuery('(prefers-color-scheme: dark)');

  // Two deps, both reactive: the user's preference and — under 'system' — the
  // OS scheme. An OS flip re-runs this computed, which is what makes the
  // `watch(resolvedAppTheme)` below the ONE path that applies the html class.
  //
  // A surface authored light-only (legal, public help, marketing, the v1 Easy
  // Data form) declares `meta.surfaceTheme: 'light'` on its route, and App.vue
  // hands the current route's declaration to `setSurfaceTheme`. While one is
  // mounted the pin outranks the preference, so the page, the document
  // backdrop and every shared component on it (menus, logos, footer) resolve
  // one theme together instead of a dark menu over a light-only page
  // (th-989tg). The preference itself is untouched, and leaving the route
  // clears the pin.
  const surfaceTheme = ref(null); // null | 'light'
  function setSurfaceTheme(theme) {
    surfaceTheme.value = theme === 'light' ? 'light' : null;
  }

  const resolvedAppTheme = computed(() => {
    if (surfaceTheme.value) return surfaceTheme.value;
    if (appTheme.value === 'system') {
      return prefersDarkScheme.value ? 'dark' : 'light';
    }
    return appTheme.value;
  });

  // Computed theme variant - controls map style and can be used for UI theming
  // Now based on app theme toggle instead of time-of-day
  const themeVariant = computed(() => resolvedAppTheme.value);

  // ============================================================================
  // VISUALIZATION SKY MANAGEMENT (controls PropertyCollectionVisualization sky only)
  // ============================================================================
  // dayPhaseMode: user's selection ('auto' uses current time, others are manual overrides)
  // timeOfDay: the actual computed value used for visualization sky colors
  const dayPhaseMode = ref('auto'); // one of DAY_PHASE_MODE_IDS
  const timeOfDay = ref('day'); // 'dawn' | 'day' | 'dusk' | 'night'
  let timeOfDayInterval = null;

  function updateTimeOfDay() {
    if (dayPhaseMode.value !== 'auto') {
      // Manual selection: use selected phase
      timeOfDay.value = dayPhaseMode.value;
      return;
    }

    // Auto mode: calculate based on current hour
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 8) timeOfDay.value = 'dawn';
    else if (hour >= 8 && hour < 18) timeOfDay.value = 'day';
    else if (hour >= 18 && hour < 20) timeOfDay.value = 'dusk';
    else timeOfDay.value = 'night';
  }

  // The ONE validated write. Every producer goes through it — the Workspace
  // settings page's sky control and `WorkspaceShell`'s restore of the
  // `workspace` URL slot — so an unknown id never reaches the ref from any
  // direction.
  function setDayPhaseMode(mode) {
    if (DAY_PHASE_MODE_IDS.includes(mode)) {
      dayPhaseMode.value = mode;
      updateTimeOfDay();
    }
  }

  function initTimeOfDay() {
    // Initialize and start the update interval
    updateTimeOfDay();
    if (!timeOfDayInterval) {
      timeOfDayInterval = setInterval(updateTimeOfDay, 60000); // Update every minute
    }
  }

  function cleanupTimeOfDay() {
    if (timeOfDayInterval) {
      clearInterval(timeOfDayInterval);
      timeOfDayInterval = null;
    }
  }

  // Watch dayPhaseMode changes to update timeOfDay (supports v-model binding)
  watch(dayPhaseMode, () => {
    updateTimeOfDay();
  });

  // ============================================================================
  // APP THEME FUNCTIONS
  // ============================================================================

  function applyAppTheme() {
    if (typeof document === 'undefined') return; // SSR guard

    const root = document.documentElement;
    if (resolvedAppTheme.value === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }

  function setAppTheme(theme) {
    const validThemes = ['light', 'dark', 'system'];
    if (!validThemes.includes(theme)) return;

    appTheme.value = theme;

    // Persist using localStorageCacheStore
    const cacheStore = useLocalStorageCacheStore();
    cacheStore.storeData(
      cacheStore.CACHE_NAMESPACES.USER_DATA,
      { setting: 'app_theme' },
      theme,
      { ttl: 365 * 24 * 60 * 60 * 1000 } // 1 year TTL for user preferences
    );
    mirrorThemePreference(theme);

    applyAppTheme();
  }

  function initAppTheme() {
    const cacheStore = useLocalStorageCacheStore();

    // Load from localStorage
    const stored = cacheStore.fetchData(
      cacheStore.CACHE_NAMESPACES.USER_DATA,
      { setting: 'app_theme' }
    );
    if (stored && ['light', 'dark', 'system'].includes(stored)) {
      appTheme.value = stored;
    }
    // A preference saved before the mirror existed reaches the pre-paint
    // script from the next visit on.
    mirrorThemePreference(appTheme.value);

    // The initial class. Every later change — a user's setAppTheme, or an OS
    // scheme flip while appTheme is 'system' — arrives through the ONE watcher
    // below, so there is no second listener to keep in step (th-z5i9t).
    applyAppTheme();
  }

  // The single path from resolvedAppTheme to the html `dark` class.
  watch(resolvedAppTheme, () => {
    applyAppTheme();
  });

  function matchLayoutToScreen() {
    // Only auto-set layout if no layout is currently set (null)
    // This prevents overwriting URL-restored layouts
    if (workspaceLayout.value === null) {
      if (isPortrait.value) {
        workspaceLayout.value = "topAndBottom";
      } else {
        workspaceLayout.value = "sideBySide";
      }
    }
  }

  function setLayout(layout) {
    if (WORKSPACE_LAYOUTS.includes(layout)) {
      workspaceLayout.value = layout
    }
  }

  // ============================================================================
  // ARTEMIS CLIENT-STATE ENVELOPE CONTRACT
  // ============================================================================
  // The following accessors are read directly by useArtemisClientState.js to
  // populate the `ephemeral` portion of the Artemis client_state envelope:
  //   - isDesktop          → ephemeral.is_desktop
  //   - appTheme           → ephemeral.app_theme           ('light'|'dark'|'system')
  //   - resolvedAppTheme   → ephemeral.resolved_app_theme  ('light'|'dark')
  //   - timeOfDay          → ephemeral.time_of_day         ('dawn'|'day'|'dusk'|'night')
  // All four are simple scalars — no snapshotForArtemis() wrapper required.
  // See openspec/changes/artemis-floating-input-hooks/design.md §6.
  //
  // `dayPhaseMode` is NOT one of them. It rides the URL as
  // `router_state.workspace.dayPhaseMode`, which the envelope already carries
  // in full, so naming it in `ephemeral` too would state the sky twice; only
  // `timeOfDay` — a derivation of the mode and the clock that no URL slot
  // holds — stays (skyline-non-leasable-plates D5).
  return {
    windowHeight,
    windowWidth,
    workspaceLayout,
    workspaceResized,
    broadcastWorkspaceResize,
    isDesktop,
    viewportMeasured,
    offeredLayouts,
    noPanelToDockIn,
    isPortrait,
    savedGridTemplateRows,
    savedGridTemplateColumns,
    // Side-by-side width option
    sideBySideWidthOption,
    SIDE_BY_SIDE_WIDTH_OPTIONS,
    setSideBySideWidthOption,
    applyGridTemplateColumns,
    applySideBySideWidthOption,
    cycleSideBySideWidth,
    // Layout selector pin
    layoutSelectorPinned,
    setLayoutSelectorPinned,
    toggleLayoutSelectorPinned,
    initLayoutSelectorPinned,
    matchLayoutToScreen,
    setLayout,
    // App theme (controls UI chrome + map style)
    appTheme,
    resolvedAppTheme,
    themeVariant,
    setAppTheme,
    initAppTheme,
    surfaceTheme,
    setSurfaceTheme,
    // Visualization sky (controls PropertyCollectionVisualization sky only)
    dayPhaseMode,
    dayPhaseOptions,
    timeOfDay,
    setDayPhaseMode,
    initTimeOfDay,
    cleanupTimeOfDay,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useWorkspaceLayoutStore, import.meta.hot)
  );
}
